import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Avatar, Alert } from '@mui/material';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import {
  getInitials,
  formatDateTime,
} from '../../../utils/helper';
import {
  getActivityConfig,
  getActivityCategory,
  ActivityIcon,
  toTitleCase,
} from '../../../utils/lead_helper_v2';
import EmptyActivityCard from '../../../components/common/EmptyActivityCard';
import { LeadTabPanelLoader, LeadTabInlineProgress } from './components/LeadTabPanelLoader';
import { useLeadTabInfiniteScroll } from './components/useLeadTabInfiniteScroll';

// ── Design tokens ──────────────────────────────────────────────────────────
const G = {
  pageBg:      '#F3F4F6',
  line:        '#E5E7EB',
  cardBg:      '#FFFFFF',
  cardBorder:  '#E9EAEC',
  textPrimary: '#111827',
  textSec:     '#6B7280',
  textMuted:   '#9CA3AF',
  monthLabel:  '#9CA3AF',
  dotBorder:   '#E5E7EB',
};

// ── Category definitions (order = chip display order) ─────────────────────
const FILTER_CATEGORIES = [
  { label: 'All',          key: 'all',          iconType: null },
  { label: 'Calls',        key: 'communication', iconType: 'call_made' },
  { label: 'Tasks',        key: 'followup',      iconType: 'followup_scheduled' },
  { label: 'Status',       key: 'status',        iconType: 'status_changed' },
  { label: 'Notes & Docs', key: 'notes',         iconType: 'note_added' },
  { label: 'Property',     key: 'property',      iconType: 'property_shared' },
  { label: 'Assignment',   key: 'assignment',    iconType: 'assigned' },
  { label: 'Requirements', key: 'requirements',  iconType: 'requirements_updated' },
  { label: 'Negotiation',  key: 'negotiation',   iconType: 'offer_made' },
  { label: 'Lost / Junk',  key: 'lost',          iconType: 'lead_lost' },
  { label: 'Lead',         key: 'lead',          iconType: 'lead_created' },
  { label: 'Partners',     key: 'channel',       iconType: 'channel_partner_created' },
];

// ── Build count map from activity list ────────────────────────────────────
const buildCounts = (activities, totalAll) => {
  const counts = { all: totalAll != null ? totalAll : activities.length };
  activities.forEach(({ action = '' }) => {
    const cat = getActivityCategory(action);
    counts[cat] = (counts[cat] || 0) + 1;
  });
  return counts;
};

// ── Group activities by "Month YYYY" ──────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const getMonthGroup = (activity) => {
  const raw = activity.activity_date || activity.created_at;
  if (!raw) return 'Unknown';
  const d = new Date(raw);
  if (isNaN(d)) return 'Unknown';
  return `${MONTH_NAMES[d.getMonth()].toUpperCase()} ${d.getFullYear()}`;
};

const groupByMonth = (activities) => {
  const groups = [];
  const seen = new Map();
  activities.forEach((a) => {
    const key = getMonthGroup(a);
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ label: key, items: [] });
    }
    groups[seen.get(key)].items.push(a);
  });
  return groups;
};

// ── Derive call/visit outcome from notes or action ────────────────────────
const getActivityDisplayDateTime = (activity) => {
  const displayDate =
    activity.activity_date ||
    (activity.created_at ? formatDateTime(activity.created_at, 'date') : 'N/A');
  const displayTime =
    activity.activity_time ||
    (activity.created_at ? formatDateTime(activity.created_at, 'time') : 'N/A');
  return { displayDate, displayTime };
};

const getOutcomeBadge = (activity) => {
  const notes = (activity.activity_notes || '').toLowerCase();
  const action = (activity.action || '').toLowerCase();

  // For call types, determine outcome
  if (action.includes('call')) {
    if (notes.includes('missed') || notes.includes('no answer') || notes.includes('duration: 0:00') || notes.includes('0:00'))
      return { label: 'Missed', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' };
    if (notes.includes('connected') || notes.includes('answered') || notes.includes('completed'))
      return { label: 'Connected', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' };
    if (notes.includes('busy'))
      return { label: 'Busy', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' };
    return null;
  }
  // For site visits
  if (action.includes('site_visit')) {
    if (action.includes('cancelled'))
      return { label: 'Cancelled', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' };
    if (action.includes('completed'))
      return { label: 'Completed', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' };
    if (action.includes('scheduled'))
      return { label: 'Scheduled', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' };
  }
  return null;
};

// ── Filter chip (palette matches ActivityCard type badge via getActivityConfig) ──
const FilterChip = ({ label, count, active, onClick, iconType }) => {
  const cfg = iconType ? getActivityConfig(iconType) : getActivityConfig('other');
  const { color, bg, border } = cfg;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        px: 1.1, py: '3px', borderRadius: '10px',
        border: `1.5px solid ${active ? color : border}`,
        background: active ? color : bg,
        cursor: 'pointer', userSelect: 'none',
        transition: 'all 0.15s ease',
        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : '0 1px 2px rgba(0,0,0,0.04)',
        '&:hover': {
          borderColor: color,
          background: active ? color : bg,
          filter: active ? 'brightness(0.92)' : 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        },
      }}
    >
      {iconType && (
        <Box sx={{
          display: 'flex', alignItems: 'center',
          color: active ? 'rgba(255,255,255,0.95)' : color,
          flexShrink: 0,
        }}>
          <ActivityIcon type={iconType} size={10} />
        </Box>
      )}
      <Typography sx={{
        fontSize: '0.625rem', fontWeight: 600, lineHeight: 1,
        color: active ? '#fff' : color,
      }}>
        {label}
      </Typography>
      <Box sx={{
        fontSize: '0.55rem', fontWeight: 700, lineHeight: 1,
        px: '4px', py: '1px', borderRadius: '10px',
        minWidth: '16px', textAlign: 'center',
        background: active ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.75)',
        color: active ? '#fff' : color,
        border: active ? 'none' : `1px solid ${border}`,
      }}>
        {count}
      </Box>
    </Box>
  );
};

// ── Timeline icon dot ──────────────────────────────────────────────────────
const TimelineDot = ({ activityType, isFirst }) => {
  const { color, bg, border } = getActivityConfig(activityType);
  return (
    <Box sx={{
      width: 36, height: 36, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      background: bg,
      border: `1.5px solid ${border}`,
      color,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <ActivityIcon type={activityType} size={14} />
    </Box>
  );
};

// ── Single activity card ───────────────────────────────────────────────────
const ActivityCard = ({ activity, showDateInHeader = true }) => {
  const activityType = activity.action || '';
  const cfg = getActivityConfig(activityType);
  const labelSource =
    cfg.label === 'Activity' && activityType
      ? activityType.replace(/_/g, ' ')
      : cfg.label;
  const label = toTitleCase(labelSource);
  const { color, bg, border } = cfg;

  const { displayDate, displayTime } = getActivityDisplayDateTime(activity);

  // Actor: prefer created_by, fall back to agent
  // If created_by is null but agent exists → automated/system action, still show agent as context
  const actorNameRaw = activity.created_by?.name || activity.agent?.name || '';
  const actorName = actorNameRaw ? toTitleCase(actorNameRaw) : '';
  const actorInitials = activity.created_by?.initials || activity.agent?.initials || '';

  const outcomeBadge = getOutcomeBadge(activity);

  const avatarColors = { bg, color };

  return (
    <Box sx={{
      background: G.cardBg,
      border: `1px solid ${G.cardBorder}`,
      borderRadius: '12px',
      px: 2.5, py: 1.75,
      transition: 'box-shadow 0.18s, border-color 0.18s',
      '&:hover': {
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        borderColor: border,
      },
    }}>
      {/* Header row: badge (+ optional date-time on the right) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: showDateInHeader ? 'space-between' : 'flex-start',
          alignItems: 'center',
          mb: 0.75,
        }}
      >
        <Box sx={{
          px: '8px', py: '3px', borderRadius: '6px',
          background: bg, border: `1px solid ${border}`,
          display: 'inline-flex', alignItems: 'center', gap: '4px',
        }}>
          <Box sx={{ color, display: 'flex', alignItems: 'center' }}>
            <ActivityIcon type={activityType} size={10} />
          </Box>
          <Typography sx={{
            fontSize: '0.65rem', fontWeight: 700, lineHeight: 1.5, color,
            letterSpacing: '0.02em',
          }}>
            {label}
          </Typography>
        </Box>

        {showDateInHeader && (
          <Typography sx={{ fontSize: '0.72rem', color: G.textMuted, flexShrink: 0 }}>
            {displayDate} · {displayTime}
          </Typography>
        )}
      </Box>

      {/* Activity notes / description */}
      <Box
        sx={{
          fontSize: '0.875rem', color: G.textPrimary,
          lineHeight: 1.6, wordBreak: 'break-word', mb: 1,
          '& strong, & b': { fontWeight: 600 },
        }}
        dangerouslySetInnerHTML={{
          __html: activity.activity_notes || '<em style="color:#9CA3AF">No description</em>',
        }}
      />

      {/* Footer: actor + outcome badge */}
      {(actorName || outcomeBadge) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
          {actorName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Avatar sx={{
                width: 22, height: 22,
                fontSize: '0.58rem', fontWeight: 700,
                bgcolor: avatarColors.bg,
                color: avatarColors.color,
                border: `1px solid ${border}`,
              }}>
                {actorInitials || getInitials(actorName)}
              </Avatar>
              <Typography sx={{ fontSize: '0.775rem', color: G.textSec, fontWeight: 500 }}>
                {actorName}
              </Typography>
              {/* Show "System" tag when created_by is null (automated action) */}
            </Box>
          )}

          {/* Separator dot between actor and outcome */}
          {actorName && outcomeBadge && (
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: G.textMuted }} />
          )}

          {/* Outcome badge e.g. Missed / Connected */}
          {outcomeBadge && (
            <Box sx={{
              px: '8px', py: '2px', borderRadius: '12px',
              background: outcomeBadge.bg,
              border: `1px solid ${outcomeBadge.border}`,
            }}>
              <Typography sx={{
                fontSize: '0.7rem', fontWeight: 600,
                color: outcomeBadge.color, lineHeight: 1.5,
              }}>
                {outcomeBadge.label}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ── Month group separator ──────────────────────────────────────────────────
const MonthDivider = ({ label }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 2, my: 2.5,
  }}>
    <Box sx={{ flex: 1, height: '1px', bgcolor: G.line }} />
    <Typography sx={{
      fontSize: '0.68rem', fontWeight: 700,
      color: G.monthLabel, letterSpacing: '0.1em',
      textTransform: 'uppercase', flexShrink: 0,
      userSelect: 'none',
    }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: G.line }} />
  </Box>
);

// ── Main component ─────────────────────────────────────────────────────────
const LeadActivity = ({ leadId, activities: activitiesProp = [], loading: loadingProp, error: errorProp }) => {
  const {
    fetchLeadActivity,
    activitiesPagination,
    loading: loadingCtx,
    error: errorCtx,
  } = useLeadDetails();

  const activities = activitiesProp;
  const loading = loadingProp ?? loadingCtx?.activities ?? false;
  const error = errorProp ?? errorCtx?.activities ?? null;

  const [activeFilter, setActiveFilter] = useState('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreLockRef = useRef(false);

  const handleLoadMore = useCallback(async () => {
    if (!leadId || loadMoreLockRef.current || !activitiesPagination?.hasNext) return;
    loadMoreLockRef.current = true;
    setLoadingMore(true);
    try {
      await fetchLeadActivity(leadId, {
        page: activitiesPagination.currentPage + 1,
        page_size: activitiesPagination.pageSize,
      });
    } catch (e) {
      console.error('Error loading more activities:', e);
    } finally {
      setLoadingMore(false);
      loadMoreLockRef.current = false;
    }
  }, [leadId, activitiesPagination, fetchLeadActivity]);

  const counts = useMemo(
    () => buildCounts(activities, activitiesPagination?.totalCount),
    [activities, activitiesPagination?.totalCount],
  );

  const visibleFilters = useMemo(
    () => FILTER_CATEGORIES.filter(({ key }) => key === 'all' || (counts[key] || 0) > 0),
    [counts],
  );

  const filtered = useMemo(() => {
    if (activeFilter === 'all') return activities;
    return activities.filter(({ action = '' }) => getActivityCategory(action) === activeFilter);
  }, [activities, activeFilter]);

  // Group by month for the timeline view
  const monthGroups = useMemo(() => groupByMonth(filtered), [filtered]);

  const activityScrollEnabled =
    Boolean(leadId) &&
    !loading &&
    activities.length > 0 &&
    Boolean(activitiesPagination?.hasNext) &&
    (activeFilter === 'all' || filtered.length > 0);

  const loadMoreSentinelRef = useLeadTabInfiniteScroll({
    enabled: activityScrollEnabled,
    hasNext: Boolean(activitiesPagination?.hasNext),
    isBusy: loading || loadingMore,
    onLoadMore: handleLoadMore,
    watchKey: `${activitiesPagination?.currentPage ?? 0}-${activities.length}`,
  });

  // ── Loading ──
  if (loading) {
    return (
      <Box sx={{ px: 2.5, pt: 2.5 }}>
        <LeadTabPanelLoader />
      </Box>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Alert severity="error" sx={{ borderRadius: '10px' }}>
          Failed to load activities. Please try again.
        </Alert>
      </Box>
    );
  }

  // ── Empty ──
  if (!activities.length) {
    return (
      <EmptyActivityCard
        title="No activities yet"
        description="Start engaging with this lead to see activity history here."
        showButton={false}
      />
    );
  }

  const activeLabel = visibleFilters.find(f => f.key === activeFilter)?.label?.toLowerCase() || '';

  return (
    <Box sx={{ p: 2.5 }}>

      {/* ── Filter bar ── */}
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 0.5,
        mb: 2.5, pb: 2,
        borderBottom: `1px solid ${G.line}`,
      }}>
        {visibleFilters.map(({ label, key, iconType }) => (
          <FilterChip
            key={key}
            label={label}
            count={key === 'all' ? counts.all : counts[key] || 0}
            active={activeFilter === key}
            iconType={key !== 'all' ? iconType : null}
            onClick={() => setActiveFilter(key)}
          />
        ))}
      </Box>

      {/* ── Empty filtered result ── */}
      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ fontSize: '0.875rem', color: G.textMuted }}>
            No {activeLabel} activities found.
          </Typography>
        </Box>
      )}

      {/* ── Month-grouped timeline ── */}
      {monthGroups.map(({ label: monthLabel, items }) => (
        <Box key={monthLabel}>
          {/* Month header divider */}
          <MonthDivider label={monthLabel} />

          {/* Activities in this month */}
          {items.map((activity, idx) => {
            const { displayDate, displayTime } = getActivityDisplayDateTime(activity);
            return (
              <Box
                key={activity.id || idx}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 1.25,
                  pb: idx < items.length - 1 ? 1.5 : 0,
                }}
              >
                {/* Left: date & time (timeline label column) */}
                <Box
                  sx={{
                    width: { xs: 80, sm: 100 },
                    flexShrink: 0,
                    pt: '10px',
                    textAlign: 'right',
                    pr: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: G.textPrimary,
                      lineHeight: 1.35,
                    }}
                  >
                    {displayDate}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 500,
                      color: G.textMuted,
                      mt: '4px',
                      lineHeight: 1.3,
                    }}
                  >
                    {displayTime}
                  </Typography>
                </Box>

                {/* Center spine: vertical line + dot (line runs through the column) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: 36,
                    flexShrink: 0,
                  }}
                >
                  {idx > 0 && (
                    <Box
                      sx={{
                        width: '2px',
                        flex: '0 0 auto',
                        height: 16,
                        bgcolor: G.line,
                        borderRadius: '1px',
                      }}
                    />
                  )}
                  <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                    <TimelineDot activityType={activity.action || ''} />
                  </Box>
                  {idx < items.length - 1 && (
                    <Box
                      sx={{
                        width: '2px',
                        flex: 1,
                        minHeight: 20,
                        bgcolor: G.line,
                        alignSelf: 'stretch',
                        mx: 'auto',
                        borderRadius: '1px',
                      }}
                    />
                  )}
                </Box>

                {/* Card */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ActivityCard activity={activity} showDateInHeader={false} />
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}

      {activitiesPagination?.hasNext && (activeFilter === 'all' || filtered.length > 0) && (
        <>
          <Box
            ref={loadMoreSentinelRef}
            sx={{ height: 4, minHeight: 4, flexShrink: 0 }}
            aria-hidden
          />
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <LeadTabInlineProgress sx={{ mr: 0 }} />
            </Box>
          )}
        </>
      )}

      {activities.length > 0 && activitiesPagination?.totalCount != null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#CBD5E1', fontWeight: 500 }}>
            Showing {activities.length} of {activitiesPagination.totalCount} activities
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LeadActivity;
